import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Form, Formik } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { ButtonV2, Card } from '@vartanainc/design-system';
import AutoLoad from '../../components/AutoLoad';
import CancelConfirmationModal from '../../components/Modals/CancelConfirmation';
import SalesRepField from '../../components/SalesRepSelect';
import SvgIcon from '../../components/SvgIcon/SvgIcon';
import {
  commonRegex,
  FIELD_INVALID_MESSAGES,
  ROLES,
} from '../../constants/common.constants';
import { TEXT_CONSTANTS } from '../../constants/ui/text.constants';
import CustomDropdown from '../../designSystem/FormikDropdownInput/FormikDropdownInput';
import PatternInput from '../../designSystem/PatternInput/PatternInput';
import TextInputField from '../../designSystem/TextInput/TextInputField';
import { sessionVar } from '../../graphql/cache';
import { USER_ROLES_FOR_COMPANY } from '../../graphql/queries/roles';
import {
  CREATE_USER,
  SALES_MANAGER_USERS,
  UPDATE_USER,
} from '../../graphql/queries/user';
import { showErrorToast, showToast } from '../../utils/helpers';
import { removeCountryCode } from '../../utils/removeCountryCodeUtils';
import './user.scss';

// Todo: Update scheme when integrating API.
const ProfileDetailsFormSchema = yup.object().shape({
  number: yup.string().nullable(),
  firstName: yup
    .string()
    .nullable()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('This field is required')
    .matches(commonRegex.ALPHABETS_AND_SINGLE_SPACE, FIELD_INVALID_MESSAGES.firstName),
  lastName: yup
    .string()
    .nullable()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('This field is required')
    .matches(commonRegex.ALPHABETS_AND_SINGLE_SPACE, FIELD_INVALID_MESSAGES.lastName),
  email: yup
    .string()
    .nullable()
    .typeError('Invalid email')
    .email('Invalid email')
    .required('This field is required'),
  title: yup.string().nullable(),
  role: yup.string().nullable().required('This field is required'),
  phone: yup.string().nullable(),
});

export const initialProfileFormValues = {
  number: '',
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phone: '',
  role: '',
};

function UpsertUser({ mode, onClose, selectedUser }) {
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(initialProfileFormValues);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [noSalesManagerInCompany, setNoSalesManagerInCompany] = useState(false);

  const session = useReactiveVar(sessionVar);

  const isSalesManagerUser = useMemo(() => {
    return get(session, 'session.user.userRole.name', null) === ROLES.SALES_MANAGER;
  }, [session]);

  const [upsertUser] = useMutation(mode === 'create' ? CREATE_USER : UPDATE_USER);
  const { loading: rolesLoading } = useQuery(USER_ROLES_FOR_COMPANY, {
    onCompleted: (response) => {
      if (!isSalesManagerUser) {
        setRoles(
          get(response, 'userRolesForCompany', []).map((role) => ({
            value: String(role.id),
            label: role.name,
          }))
        );
      }
    },
  });

  const salesManagerRole = useMemo(() => {
    const salesManager = roles.find((role) => role.label === ROLES.SALES_MANAGER);
    return salesManager?.value;
  }, [roles]);

  const salesRepRole = useMemo(() => {
    const salesRep = roles.find((role) => role.label === ROLES.SALES);
    return salesRep?.value;
  }, [roles]);

  const { loading: salesMananagerRoleLoading } = useQuery(USER_ROLES_FOR_COMPANY, {
    onCompleted: (response) => {
      if (isSalesManagerUser) {
        setRoles(
          get(response, 'userRolesForCompany', []).map((role) => ({
            value: `${role.id}`,
            label: role.name,
          }))
        );
      }
    },
  });

  const isCreateMode = useMemo(() => mode === 'create', [mode]);

  const { loading: managersLoading } = useQuery(
    SALES_MANAGER_USERS,
    {
      onCompleted: (response) => {
        const users = get(response, 'salesManagerUsers', []);
        setNoSalesManagerInCompany(users.length === 0);
        let salesManagersData = users.reduce((acc, user) => {
          // filter out self user from the list
          if (user.number !== selectedUser.number) {
            acc.push({
              value: user.id,
              label: user?.fullName || '',
            });
          }
          return acc;
        }, []);
        if (!isCreateMode) {
          salesManagersData = [{ value: null, label: 'None' }, ...salesManagersData];
        }
        setSalesManagers(salesManagersData);
      },
    },
    {
      variables: {
        roleId: parseInt(salesManagerRole, 10),
      },
    }
  );

  useEffect(() => {
    if (Object.keys(selectedUser).length) {
      setInitialFormValues({
        ...initialProfileFormValues,
        ...selectedUser,
        managerId: selectedUser?.manager?.id || null,
        phone: removeCountryCode(selectedUser.phone),
        role: get(selectedUser, 'userRole.id') || '',
      });
    } else if (isSalesManagerUser) {
      // if sales manager is creating a user we should auto-set manager and role fields
      setInitialFormValues({
        ...initialProfileFormValues,
        role: salesRepRole,
        managerId: session.session.user.id,
      });
    }
  }, [selectedUser, session, salesRepRole, isSalesManagerUser]);

  const handleSubmit = (values, { setErrors }) => {
    setIsFormSubmitting(true);
    let managerOption = null;
    if (values.managerId) {
      managerOption = salesManagers.find((option) => option.value === values.managerId);
    }
    const managerId = managerOption ? +managerOption.value : null;

    upsertUser({
      variables: {
        ...values,
        roleId: +values.role,
        id: +values.id,
        phone: values.phone === '' ? null : values.phone, // with the new PhoneInput component we will only have empty values
        users:
          values.role === salesManagerRole
            ? values.users?.map((user) => +user.value)
            : [],
        managerId,
      },
    })
      .then((response) => {
        const errors = get(response, 'errors', {});
        if (Object.keys(errors).length) {
          setErrors(errors);
        } else {
          const successToastMsg =
            mode === 'create'
              ? `${values.firstName} ${values.lastName} has been added successfully. An email has been sent with instructions to activate their account.`
              : 'Information updated successfully.';
          showToast('success', successToastMsg);
          if (mode === 'create') {
            navigate('/dashboard/settings/users');
          } else {
            onClose({
              ...values,
              fullName: `${values.firstName} ${values.lastName}`,
              manager: {
                id: managerOption?.value,
                fullName: managerOption?.label,
              },
              userRole: {
                id: values.role,
                name: roles.find((role) => role.value === values.role).label,
              },
            });
          }
        }
      })
      .catch(() => {
        showErrorToast();
      })
      .finally(() => setIsFormSubmitting(false));
  };

  const handleCancel = () => {
    if (mode === 'create') {
      navigate('/dashboard/settings/users');
    } else {
      onClose();
    }
  };

  const handleRoleChange = async (option, setFieldValue, validateForm) => {
    // whenever role is set to 'Sales' check if the user is a sales manager and set its id as managerId
    if (option.value === salesRepRole && isSalesManagerUser) {
      await setFieldValue('managerId', session.session.user.id);
    }
    // if the role is not sales manager then set the managerId to null
    if (option.value !== salesRepRole) {
      await setFieldValue('managerId', null);
    }
    validateForm();
  };

  const showSalesRepField = useMemo(() => {
    return isCreateMode && !isSalesManagerUser;
  }, [isCreateMode, isSalesManagerUser]);

  return (
    <div className={mode === 'create' ? 'p-10' : ''}>
      <Formik
        initialValues={initialFormValues}
        enableReinitialize
        validationSchema={ProfileDetailsFormSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, values, setFieldValue, validateForm }) => (
          <Form className="create-user-container">
            <Card
              tabindex={0}
              title={mode === 'create' ? 'Add a new user' : 'Edit user details'}
              icon={<SvgIcon name="badge" fill="color-blue-120" className="w-8" />}
              content={(
                <div>
                  <CancelConfirmationModal
                    open={openConfirmationModal}
                    onClose={() => setOpenConfirmationModal(false)}
                    onConfirm={() => handleCancel()}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <TextInputField name="firstName" label="First Name" />
                    <TextInputField name="lastName" label="Last Name" />
                    <TextInputField
                      name="email"
                      label="Email / User ID"
                      placeholder="example@email.com"
                      disabled={mode === 'edit'}
                    />
                    <PatternInput
                      name="phone"
                      label="Phone"
                      mask="_"
                      value={values.phone}
                    />
                    <TextInputField name="title" label="Job Title" />
                    <CustomDropdown
                      name="role"
                      label="Role"
                      options={roles}
                      isLoading={rolesLoading || salesMananagerRoleLoading}
                      disabled={isSalesManagerUser}
                      placeholder="Select"
                      onChange={(option) => {
                        handleRoleChange(option, setFieldValue, validateForm);
                      }}
                    />
                  </div>
                  {showSalesRepField && values.role === salesManagerRole && (
                    <div className="mt-4">
                      <SalesRepField
                        name="users"
                        label={TEXT_CONSTANTS.ASSIGN_SALES_REP}
                        placeholder={TEXT_CONSTANTS.ASSIGN_SALES_REP_PLACEHOLDER}
                      />
                    </div>
                  )}
                  {values.role === salesRepRole && !noSalesManagerInCompany && (
                    <div className="mt-4">
                      <CustomDropdown
                        name="managerId"
                        label="Assign sales manager (optional)"
                        options={salesManagers}
                        isLoading={managersLoading}
                        placeholder="Select"
                        disabled={isSalesManagerUser}
                      />
                    </div>
                  )}

                  <div className="flex flex-row justify-between mt-8">
                    <ButtonV2
                      variant={{ type: 'ghost', typography: 'paragraph14' }}
                      type="button"
                      text="Cancel"
                      iconLeft="chevron_left"
                      onClick={() => {
                        if (dirty) {
                          setOpenConfirmationModal(true);
                        } else handleCancel();
                      }}
                    />
                    <AutoLoad loading={isFormSubmitting}>
                      <ButtonV2
                        type="submit"
                        disabled={!isValid || !dirty}
                        text="Submit"
                        variant={{ type: 'primary', typography: 'paragraph14' }}
                      />
                    </AutoLoad>
                  </div>
                </div>
              )}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}

UpsertUser.propTypes = {
  onClose: PropTypes.func,
  mode: PropTypes.oneOf(['edit', 'create']),
  selectedUser: PropTypes.shape({
    number: PropTypes.string,
    phone: PropTypes.string,
    manager: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

UpsertUser.defaultProps = {
  onClose: () => {},
  mode: 'create',
  selectedUser: {},
};

export default UpsertUser;
